import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getDevices,
  getAllEvents,
  createDevice,
  updateDevice,
  deleteDevice,
  getMealTypes,
} from "../services/api";
import { useApiQuery, useApiMutation } from "../hooks/useApiQuery";
import {
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
  CakeIcon,
} from "@heroicons/react/24/outline";
import { useSocket } from "../context/SocketContext";
import useToast from "../hooks/useToast";

const DevicesByEvent = () => {
  const socket = useSocket();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [deletingDeviceId, setDeletingDeviceId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);

  const [formData, setFormData] = useState({
    deviceId: "",
    name: "",
    eventId: "",
    status: "active",
    mealTypeId: "",
  });

  const {
    data: allDevices = [],
    isLoading: devicesLoading,
    refetch: refetchDevices,
  } = useApiQuery(
    ["devices"],
    async () => {
      const res = await getDevices();
      if (Array.isArray(res?.data)) return res.data;
      if (Array.isArray(res?.data?.data)) return res.data.data;
      return [];
    },
    { staleTime: 5 * 60 * 1000 },
  );

  const {
    data: allEvents,
    isLoading: eventsLoading,
    isError: eventsError,
  } = useApiQuery(
    ["all-events"],
    async () => {
      const res = await getAllEvents();
      if (Array.isArray(res?.data)) return res.data;
      if (Array.isArray(res?.data?.data)) return res.data.data;
      return [];
    },
    { staleTime: 5 * 60 * 1000 },
  );

  const { data: mealTypes = [] } = useApiQuery(
    ["meal-types", eventId],
    () => getMealTypes(eventId).then((r) => r.data),
    { enabled: Boolean(eventId), staleTime: 5 * 60 * 1000 },
  );

  const normalizedEvents = useMemo(() => {
    if (Array.isArray(allEvents)) return allEvents;
    if (Array.isArray(allEvents?.data)) return allEvents.data;
    return [];
  }, [allEvents]);

  const normalizedDevices = useMemo(() => {
    if (Array.isArray(allDevices)) return allDevices;
    if (Array.isArray(allDevices?.data)) return allDevices.data;
    return [];
  }, [allDevices]);

  const devices = useMemo(() => {
    if (!Array.isArray(normalizedDevices)) return [];
    return normalizedDevices
      .filter((d) => {
        const eid = d?.eventId?._id || d?.eventId;
        return String(eid) === String(eventId);
      })
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [normalizedDevices, eventId]);

  const eventName = useMemo(() => {
    if (!Array.isArray(normalizedEvents)) return "";
    return (
      normalizedEvents.find((e) => String(e?._id) === String(eventId))?.name ||
      ""
    );
  }, [normalizedEvents, eventId]);

  const isLoading = devicesLoading || eventsLoading;

  const createMutation = useApiMutation(
    (data) => createDevice(data).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess("Device created successfully");
        handleCloseModal();
        refetchDevices();
      },
      onError: (e) =>
        showError(e.response?.data?.message || "Failed to create device"),
    },
  );

  const updateMutation = useApiMutation(
    ({ id, data }) => updateDevice(id, data).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess("Device updated successfully");
        handleCloseModal();
        refetchDevices();
      },
      onError: (e) =>
        showError(e.response?.data?.message || "Failed to update device"),
    },
  );

  const deleteMutation = useApiMutation(
    (id) => deleteDevice(id).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess("Device deleted successfully");
        refetchDevices();
      },
      onError: (e) =>
        showError(e.response?.data?.message || "Failed to delete device"),
    },
  );

  useEffect(() => {
    if (!socket) return;
    const refresh = () => refetchDevices();
    socket.on("deviceCreated", refresh);
    socket.on("deviceUpdated", refresh);
    socket.on("deviceDeleted", refresh);
    return () => {
      socket.off("deviceCreated", refresh);
      socket.off("deviceUpdated", refresh);
      socket.off("deviceDeleted", refresh);
    };
  }, [socket, refetchDevices]);

  const emptyForm = useCallback(
    () => ({
      deviceId: "",
      name: "",
      eventId: eventId,
      status: "active",
      mealTypeId: "",
    }),
    [eventId],
  );
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingDevice(null);
    setFormData(emptyForm());
  }, [emptyForm]);

  const handleCreate = useCallback(() => {
    setEditingDevice(null);
    setFormData(emptyForm());
    setShowModal(true);
  }, [emptyForm]);

  const handleEdit = useCallback((device) => {
    setEditingDevice(device);
    setFormData({
      deviceId: device.deviceId,
      name: device.name,
      eventId: device.eventId?._id || device.eventId || "",
      status: device.status,
      mealTypeId: device.mealTypeId || "",
    });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      if (!id) return showError("Device ID is missing");
      if (!window.confirm("Delete this device?")) return;
      setDeletingDeviceId(id);
      deleteMutation.mutate(id, {
        onSettled: () => setDeletingDeviceId(null),
      });
    },
    [deleteMutation, showError],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const payload = {
        deviceId: formData.deviceId.trim(),
        name: formData.name.trim(),
        eventId: formData.eventId,
        status: formData.status,
        mealTypeId: formData.mealTypeId || null,
      };

      if (editingDevice) {
        updateMutation.mutate({ id: editingDevice._id, data: payload });
      } else {
        createMutation.mutate(payload);
      }
    },
    [editingDevice, formData, createMutation, updateMutation],
  );

  const handleViewSessions = useCallback(
    (id) => {
      if (!id) return showError("Device ID is missing");
      navigate(`/devices/${id}/sessions`);
    },
    [navigate, showError],
  );

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1471b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-[#708090] text-sm">Loading devices...</p>
        </div>
      </div>
    );
  }

  if (eventsError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-3">
            Failed to load event information.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-['Barlow']">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-[5px] text-[#708090] hover:text-[#1471b9] hover:bg-[#f6f9fc]"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-normal leading-9 text-[#2b3445]">
            Devices – {eventName || "Event"}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-sm text-[#708090] bg-[#f6f9fc] px-3 py-1.5 rounded-full border border-[#dae1e7] inline-flex items-center gap-1">
            <DevicePhoneMobileIcon className="w-4 h-4 text-[#1471b9]" />
            <span className="font-semibold text-[#2b3445]">
              {devices.length}
            </span>
            <span>device{devices.length !== 1 ? "s" : ""}</span>
          </div>

          <button
            onClick={handleCreate}
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-bold hover:opacity-90 disabled:opacity-50"
          >
            + Add Device
          </button>
        </div>
      </div>

      {/* Event badge */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] p-5">
        <div className="flex items-center gap-2">
          <DevicePhoneMobileIcon className="w-5 h-5 text-[#1471b9]" />
          <div>
            <p className="text-xs text-[#708090]">Event</p>
            <p className="text-base font-semibold text-[#2b3445]">
              {eventName || "Unknown Event"}
            </p>
          </div>
        </div>
      </div>

      {/* Devices grid */}
      {devices.length === 0 ? (
        <div className="bg-white border border-[#dae1e7] rounded-[15px] p-10 text-center">
          <DevicePhoneMobileIcon className="w-12 h-12 mx-auto text-[#b7c0ca] mb-3" />
          <h3 className="text-lg font-semibold text-[#2b3445] mb-1">
            No devices found
          </h3>
          <p className="text-sm text-[#708090] mb-5">
            Add a device to start scanning.
          </p>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-bold hover:opacity-90"
          >
            + Add Device
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {devices.map((device) => {
            const hasMeal = Boolean(device.mealTypeId);

            return (
              <div
                key={device._id}
                className="bg-white rounded-[15px] border border-[#dae1e7] overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="w-10 h-10 rounded-[10px] bg-[#f0f7fc] flex items-center justify-center">
                      <DevicePhoneMobileIcon className="w-5 h-5 text-[#1471b9]" />
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        device.status === "active"
                          ? "bg-green-50 text-green-600"
                          : "bg-red-50 text-red-600"
                      }`}
                    >
                      {device.status || "unknown"}
                    </span>
                  </div>

                  <h3 className="text-[17.6px] font-semibold text-[#2b3445] mb-1">
                    {device.name || "Unnamed Device"}
                  </h3>

                  <div className="flex items-center justify-between gap-2 mb-3">
                    <p className="text-sm text-[#708090]">
                      ID: {device.deviceId || "N/A"}
                    </p>

                    {hasMeal && (
                      <span className="inline-flex items-center gap-1 bg-[#f0f7fc] text-[#1471b9] text-xs font-medium px-2.5 py-1 rounded-full shrink-0">
                        <CakeIcon className="w-3 h-3" />
                        {device.mealTypeName}
                      </span>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-[#dae1e7]">
                    <button
                      onClick={() => handleViewSessions(device._id)}
                      disabled={isSubmitting}
                      className="bg-[#1672B9] text-white text-sm font-bold py-2 rounded-[8px] hover:opacity-90 disabled:opacity-50"
                    >
                      Sessions
                    </button>
                    <button
                      onClick={() => handleEdit(device)}
                      disabled={isSubmitting}
                      className="bg-[#1672B9] text-white text-sm font-bold py-2 rounded-[8px] hover:opacity-90 disabled:opacity-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(device._id)}
                      disabled={deletingDeviceId === device._id}
                      className="bg-[#1672B9] text-white text-sm font-bold py-2 rounded-[8px] hover:opacity-90 disabled:opacity-50"
                    >
                      {deletingDeviceId === device._id ? "..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =================================================
          Create / Edit Modal
      ================================================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-[15px] shadow-xl w-[520px] max-w-full my-8">
            <div className="p-5 border-b border-[#dae1e7]">
              <h2 className="text-[19.2px] font-semibold text-[#2b3445]">
                {editingDevice ? "Edit Device" : "Create Device"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                  Device ID
                </label>
                <input
                  type="text"
                  placeholder="Enter device ID"
                  value={formData.deviceId}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, deviceId: e.target.value }))
                  }
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20 focus:border-[#1471b9]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                  Device Name
                </label>
                <input
                  type="text"
                  placeholder="Enter device name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, name: e.target.value }))
                  }
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20 focus:border-[#1471b9]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, status: e.target.value }))
                  }
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20 focus:border-[#1471b9]"
                  disabled={isSubmitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* ============================================
                  MEAL DEVICE TOGGLE
              ============================================ */}
              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                  Meal Served (optional)
                </label>
                <select
                  value={formData.mealTypeId || ""}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, mealTypeId: e.target.value }))
                  }
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1471b9]/20 focus:border-[#1471b9]"
                  disabled={isSubmitting}
                >
                  <option value="">
                    Regular Device
                  </option>
                  {mealTypes.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.name}
                      {/* {m.price ? ` · ₹${m.price}` : ""} */}
                    </option>
                  ))}
                </select>
                {/* <p className="text-xs text-[#708090] mt-1">
                  Scans on this device will only redeem this one meal
                </p> */}
                {mealTypes.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No meals configured for this event yet
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2b3445] mb-1">
                  Event
                </label>
                <input
                  type="text"
                  value={eventName || "Current Event"}
                  className="w-full border border-[#dae1e7] bg-[#f6f9fc] rounded-[8px] px-3 py-2 text-sm text-[#708090]"
                  disabled
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-[#dae1e7] rounded-[8px] text-sm font-bold text-[#2b3445] hover:bg-[#f6f9fc] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-bold hover:opacity-90 disabled:opacity-50"
                >
                  {isSubmitting
                    ? "Saving..."
                    : editingDevice
                      ? "Update"
                      : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesByEvent;
